import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FolderPlus, Trash2, Loader2, Check, Pencil, X } from "lucide-react";

export default function CategoryManager({ open, onClose, onChanged }) {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const notify = () => onChanged && onChanged();

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await api.post("/categories", { name: name.trim() });
      toast.success("Category created");
      setName("");
      load();
      notify();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/categories/${id}`, { name: editName.trim() });
      toast.success("Category renamed");
      setEditId(null);
      load();
      notify();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to rename");
    }
  };

  const handleDelete = async (cat) => {
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success("Category deleted");
      load();
      notify();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-surface border border-white/15 rounded-none max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="categories-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-700 text-2xl tracking-tight text-white uppercase">
            Categories
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-white/40">
            Organize the vault. Deleting a category moves its collectibles to "Uncategorized".
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-6">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-glow-primary">New Category</label>
            <div className="flex gap-2 mt-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="e.g. Artifacts"
                className="bg-transparent border-white/20 text-white rounded-none focus:border-glow-primary focus-visible:ring-1 focus-visible:ring-glow-primary font-mono"
                data-testid="category-name-input"
              />
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-glow-primary text-black font-bold hover:bg-glow-primary/80 rounded-none font-mono text-xs uppercase tracking-wider shrink-0"
                data-testid="category-create-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FolderPlus className="w-4 h-4 mr-2" /> Add</>}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-glow-primary" />
            </div>
          ) : (
            <div>
              <p className="font-mono text-xs uppercase tracking-wider text-white/40 mb-3">
                All Categories ({categories.length})
              </p>
              {categories.length === 0 ? (
                <p className="font-mono text-sm text-white/40 border border-dashed border-white/15 p-6 text-center">
                  No categories yet.
                </p>
              ) : (
                <div className="space-y-2" data-testid="categories-list">
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between border border-white/10 p-3 gap-3"
                      data-testid={`category-${cat.id}`}
                    >
                      {editId === cat.id ? (
                        <>
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && saveEdit(cat.id)}
                            className="bg-transparent border-white/20 text-white rounded-none h-8 font-mono"
                            data-testid={`category-edit-input-${cat.id}`}
                            autoFocus
                          />
                          <div className="flex gap-1 shrink-0">
                            <Button onClick={() => saveEdit(cat.id)} size="sm" className="bg-glow-primary text-black rounded-none h-8" aria-label="Save" data-testid={`category-save-${cat.id}`}>
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button onClick={() => setEditId(null)} variant="outline" size="sm" className="border-white/20 text-white/60 rounded-none h-8" aria-label="Cancel">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="font-mono text-sm text-white truncate">{cat.name}</span>
                          <div className="flex gap-1 shrink-0">
                            <Button
                              onClick={() => { setEditId(cat.id); setEditName(cat.name); }}
                              variant="outline"
                              size="sm"
                              className="border-glow-primary/50 text-glow-primary hover:bg-glow-primary/10 rounded-none h-8"
                              aria-label="Rename category"
                              data-testid={`category-edit-${cat.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              onClick={() => handleDelete(cat)}
                              variant="outline"
                              size="sm"
                              className="border-glow-secondary/50 text-glow-secondary hover:bg-glow-secondary/10 rounded-none h-8"
                              aria-label="Delete category"
                              data-testid={`category-delete-${cat.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
